import { useState } from 'react';
import { BrainCircuit, BriefcaseBusiness, CalendarDays, CheckCircle2, ClipboardList, FileText, FileSpreadsheet, FolderKanban, Gavel, ListChecks, ShieldCheck, Sparkles, Users, X } from 'lucide-react';
import Header from '../layout/Header';
import AikupCard from '../components/AikupCard';
import CadreReferentielModal from '../components/CadreReferentielModal';
import GouvernanceAgenticAIModal from '../components/GouvernanceAgenticAIModal';
import GouvernanceAgentiqueV2Modal from '../components/GouvernanceAgentiqueV2Modal';
import GouvernanceTIModal from '../components/GouvernanceTIModal';
import PresentationDirecteurTIModal from '../components/PresentationDirecteurTIModal';
import PresentationPRIsMEModal from '../components/PresentationPRIsMEModal';
import ProjetEHTangiblesModal from '../components/ProjetEHTangiblesModal';
import PriseDeBesoinWizardModal from '../components/PriseDeBesoinWizardModal';
import { aikupData } from '../data/aikupData';

const PROJECT_SIGNALS = [
  {
    title: 'Portefeuille PRIsME',
    description: 'Vue opérationnelle dédiée aux projets PRIsME pilotés ou suivis par Eric Hains.',
    icon: FolderKanban,
  },
  {
    title: 'Priorités',
    description: 'Espace prévu pour regrouper les priorités, échéances et décisions à suivre.',
    icon: ListChecks,
  },
  {
    title: 'Responsables',
    description: 'Suivi des owners, collaborateurs et points de coordination.',
    icon: Users,
  },
  {
    title: 'Échéances',
    description: 'Repères de dates clés pour garder les engagements visibles.',
    icon: CalendarDays,
  },
];

const RIGUEUR_BEHAVIORS = [
  {
    title: 'Toujours transformer une idée en trace exploitable',
    description: 'Ne pas garder les idées importantes dans ta tête. Chaque décision, risque, action ou orientation doit finir dans PRIsME: tâche, note, décision de réunion, commentaire ou document. Règle simple: si ça peut être oublié, mal compris ou contesté plus tard, ça doit être écrit.',
    raison:
      'Ce comportement protège ton travail contre l’oubli, l’urgence et les interprétations différentes. Dans PRIsME, une idée non écrite reste fragile: elle dépend de ta mémoire, de ton énergie du moment et de la compréhension des autres.',
    miseEnOeuvre:
      'À chaque idée importante, crée immédiatement une trace exploitable: une tâche, une note, un commentaire ou une décision. Ajoute le contexte, le résultat attendu, le lien avec le dossier et la prochaine action. Si l’idée revient en réunion ou en suivi, rattache-la à la trace existante au lieu de repartir une conversation à zéro.',
  },
  {
    title: 'Clarifier le prochain geste à chaque sujet',
    description: 'Pour chaque dossier, toujours avoir un responsable, une prochaine action claire, une date ou un horizon, un statut réel et un blocage si ça n’avance pas. Pas seulement "à suivre": quelqu’un doit savoir quoi faire après.',
    raison:
      'La rigueur apparaît quand un dossier peut avancer sans que tout le monde doive redemander quoi faire. Le prochain geste transforme un sujet vague en responsabilité concrète.',
    miseEnOeuvre:
      'Avant de quitter une tâche, une réunion ou une conversation, inscris toujours qui agit, quoi faire, pour quand et ce qui bloque. Remplace les statuts flous comme "à suivre" par une action observable: valider, appeler, produire, décider, escalader ou fermer.',
  },
  {
    title: 'Fermer les boucles',
    description: 'Quand tu demandes quelque chose, tu dois avoir un mécanisme de retour: demandé le, attendu pour quand, relancé quand, reçu ou non, décision prise après réception. Le suivi ne doit pas vivre seulement dans les conversations.',
    raison:
      'Une demande ouverte trop longtemps crée de la perte de contrôle. Fermer les boucles évite que les suivis disparaissent dans Teams, les courriels ou les conversations rapides.',
    miseEnOeuvre:
      'Quand tu demandes quelque chose, note la date de demande, la personne responsable, l’échéance attendue et la prochaine relance. À la réception, ajoute la conclusion: reçu, incomplet, validé, refusé ou transformé en nouvelle action.',
  },
  {
    title: 'Séparer décision, discussion et action',
    description: 'Dans les réunions PRIsME, distinguer systématiquement discussion, décision, action et risque. Une discussion explique le contexte, une décision engage l’équipe, une action crée du travail et un risque demande surveillance.',
    raison:
      'Les réunions perdent de la valeur quand tout est mélangé. Cette séparation rend les comptes rendus plus solides et permet de savoir ce qui a vraiment été décidé.',
    miseEnOeuvre:
      'Dans chaque suivi, classe les éléments sous quatre libellés simples: Discussion, Décision, Action, Risque. Une décision doit être formulée clairement. Une action doit avoir un responsable. Un risque doit avoir un impact possible et un suivi prévu.',
  },
  {
    title: 'Travailler avec des critères de terminé',
    description: 'Avant de dire qu’une tâche est complétée, définir ce que "terminé" veut dire: livré où, validé par qui, documenté comment, communiqué à qui, avec quelle preuve ou quel lien disponible.',
    raison:
      'Sans critère de terminé, une tâche peut sembler finie pour une personne et incomplète pour une autre. Les critères réduisent les reprises, les malentendus et les livrables faibles.',
    miseEnOeuvre:
      'Ajoute une courte définition de terminé aux tâches importantes: livrable attendu, preuve, validation, communication et emplacement final. Ne ferme pas la tâche tant que ces éléments ne sont pas présents ou explicitement exclus.',
  },
  {
    title: 'Faire une revue hebdomadaire courte, mais sacrée',
    description: 'Chaque semaine dans PRIsME, revoir ce qui a avancé, ce qui bloque, ce qui est en retard, ce qui doit être escaladé, ce qui peut être supprimé ou reporté, puis fixer les trois priorités de la semaine suivante.',
    raison:
      'La rigueur ne vient pas seulement des gros efforts, elle vient surtout de la cadence. Une revue hebdomadaire empêche les dossiers de vieillir en silence.',
    miseEnOeuvre:
      'Bloque un moment fixe chaque semaine. Passe les dossiers en revue avec cinq questions: qu’est-ce qui a avancé, qu’est-ce qui bloque, qu’est-ce qui est en retard, qu’est-ce qui doit être escaladé, quelles sont les trois priorités de la prochaine semaine.',
  },
  {
    title: 'Mettre les risques tôt, pas quand ils explosent',
    description: 'Dès qu’un sujet a une dépendance, un flou, un retard ou une personne clé absente, le nommer comme risque. Le comportement de rigueur consiste à dire ce qui pourrait déraper avant que ça dérape.',
    raison:
      'Nommer un risque tôt donne du temps pour agir. Attendre que le problème soit visible pour tout le monde transforme souvent un petit risque en urgence.',
    miseEnOeuvre:
      'Ajoute un risque dès qu’il y a une dépendance, un flou, un retard, une capacité manquante ou une décision non prise. Précise l’impact possible, la probabilité, le propriétaire et le prochain geste pour réduire ou surveiller le risque.',
  },
  {
    title: 'Utiliser PRIsME comme système de pilotage, pas comme archive',
    description: 'Chaque jour, vérifier si PRIsME reflète la réalité actuelle, si un autre gestionnaire pourrait comprendre le dossier sans t’appeler, et si les priorités visibles sont les vraies priorités.',
    raison:
      'Une archive explique le passé. Un système de pilotage aide à décider aujourd’hui. PRIsME doit montrer l’état réel des dossiers, pas seulement conserver des informations après coup.',
    miseEnOeuvre:
      'Chaque jour, vérifie les dossiers actifs: statut réel, action suivante, risque, échéance et responsable. Si une personne externe au dossier ne comprendrait pas où ça en est, ajoute le contexte manquant.',
  },
  {
    title: 'Standardiser tes livrables',
    description: 'Pour les rapports, suivis, audits, comités et comptes rendus, garder toujours la même structure: contexte, constats, risques, décisions, actions, responsables, échéances et prochaine étape.',
    raison:
      'La standardisation donne une signature de qualité. Les gens savent où regarder, les oublis diminuent et la perception de sérieux augmente immédiatement.',
    miseEnOeuvre:
      'Utilise le même squelette pour tes rapports et suivis: contexte, constats, risques, décisions, actions, responsables, échéances, prochaine étape. Garde ce format même pour les versions courtes afin de créer un réflexe stable.',
  },
  {
    title: 'Faire moins de "je vais m’en occuper" et plus de "voici le système"',
    description: 'La vraie rigueur PRIsME consiste à déplacer le travail de ta tête vers un système visible. PRIsME doit porter la méthode, pas seulement toi.',
    raison:
      'Quand tout repose sur toi, le système devient fragile. La rigueur durable apparaît quand la méthode survit à l’absence, à la fatigue et au changement de priorité.',
    miseEnOeuvre:
      'Quand tu prends un sujet, demande-toi comment PRIsME peut le porter: modèle, checklist, tâche récurrente, gabarit, statut, décision ou indicateur. Ton objectif est que le dossier reste compréhensible et actionnable même si tu n’es pas dans la conversation.',
  },
];

export default function ProjetEH() {
  const [isCadreReferentielOpen, setIsCadreReferentielOpen] = useState(false);
  const [isTangiblesOpen, setIsTangiblesOpen] = useState(false);
  const [isTestModeleOdsOpen, setIsTestModeleOdsOpen] = useState(false);
  const [isPriseBesoinClientOpen, setIsPriseBesoinClientOpen] = useState(false);
  const [isGouvernanceAgentiqueOpen, setIsGouvernanceAgentiqueOpen] = useState(false);
  const [isGouvernanceAgentiqueV2Open, setIsGouvernanceAgentiqueV2Open] = useState(false);
  const [isGouvernanceTIOpen, setIsGouvernanceTIOpen] = useState(false);
  const [isPresentationPRIsMEOpen, setIsPresentationPRIsMEOpen] = useState(false);
  const [isPresentationDirecteurTIOpen, setIsPresentationDirecteurTIOpen] = useState(false);
  const [isRigueurOpen, setIsRigueurOpen] = useState(false);
  const cadreReferentielBZ = aikupData.find((item) => item.id === 6);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        title="Projet PRIsME"
        subtitle="Espace opérationnel dédié au suivi des projets PRIsME."
      />

      <main className="max-w-7xl mx-auto px-8 pb-12 space-y-8">
        <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-bz-blue mb-3">
              Nouveau module opérationnel
            </p>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Tableau de bord Projet PRIsME
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Cette section est prête à recevoir le suivi des projets, tâches, décisions et
              indicateurs que tu voudras centraliser dans PRIsME BZ.
            </p>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {PROJECT_SIGNALS.map((signal) => {
            const Icon = signal.icon;
            return (
              <article
                key={signal.title}
                className="bg-white border border-slate-200 rounded-xl shadow-sm p-5"
              >
                <div className="w-10 h-10 rounded-lg bg-bz-blue/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-bz-blue" />
                </div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">{signal.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{signal.description}</p>
              </article>
            );
          })}
        </section>

        {cadreReferentielBZ && (
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-linear-to-r from-bz-blue/40 to-transparent" />
              <h2 className="text-lg font-semibold text-slate-800 px-4">Référentiel</h2>
              <div className="flex-1 h-px bg-linear-to-l from-bz-blue/40 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              <AikupCard
                item={cadreReferentielBZ}
                onClick={() => setIsCadreReferentielOpen(true)}
                onExpertClick={() => {}}
                index={0}
              />

              <button
                type="button"
                onClick={() => setIsTangiblesOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-emerald-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center mb-5 group-hover:bg-emerald-600 transition-colors">
                    <FileSpreadsheet className="w-6 h-6 text-emerald-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Tableau des tangibles
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Tableau des livrables et tactiques avec association aux 6 piliers du cadre client BZ
                    et badges pour les dimensions transversales.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsTestModeleOdsOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-blue-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-5 group-hover:bg-blue-600 transition-colors">
                    <FileText className="w-6 h-6 text-blue-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Test modele ODS
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Formulaire ODS Support &amp; gestion TI lié au gabarit
                    ods-support-gestion de la gestion documentaire.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsRigueurOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-violet-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center mb-5 group-hover:bg-violet-600 transition-colors">
                    <ListChecks className="w-6 h-6 text-violet-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Ébauche de rigueur
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Référentiel de comportements observables pour augmenter la rigueur du travail:
                    traces, suivis, décisions, risques, critères de terminé et rituels de pilotage.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsPriseBesoinClientOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-amber-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-5 group-hover:bg-amber-600 transition-colors">
                    <ClipboardList className="w-6 h-6 text-amber-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    prise de besoin
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Formulaire Prise de besoin - Maturité et besoins client lié au gabarit
                    prise-de-besoin-client-2026 de la gestion documentaire.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsGouvernanceAgentiqueOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-indigo-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mb-5 group-hover:bg-indigo-600 transition-colors">
                    <ShieldCheck className="w-6 h-6 text-indigo-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Gouvernance Agentique
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Cadre appuyé sur NIST AI RMF, NIST GenAI Profile, ISO/IEC 42001 et OWASP pour
                    encadrer les agents IA, leurs accès, leurs outils, leurs données et leur autonomie.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsGouvernanceAgentiqueV2Open(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-cyan-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center mb-5 group-hover:bg-cyan-600 transition-colors">
                    <BrainCircuit className="w-6 h-6 text-cyan-700 group-hover:text-white transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Gouvernance Agentique V2
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Plan de gouvernance IA pour un client comptable, avec NIST AI RMF, Loi 25,
                    RAG contrôlé, agents spécialisés et Eric Hains comme responsable BZ.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsPresentationPRIsMEOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-slate-400 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center mb-5 group-hover:bg-black transition-colors">
                    <Sparkles className="w-6 h-6 text-cyan-200 transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    présentation PRIsME
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Expérience exécutive premium inspirée du PowerPoint PRIsME : vision, gouvernance,
                    cadre, valeur client, valeur BZ et roadmap 2026.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsPresentationDirecteurTIOpen(true)}
                className="group bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-lg hover:border-emerald-300 transition-all text-left overflow-hidden"
              >
                <div className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-emerald-950 flex items-center justify-center mb-5 group-hover:bg-emerald-800 transition-colors">
                    <BriefcaseBusiness className="w-6 h-6 text-emerald-200 transition-colors" />
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Présentation Directeur TI
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Guide premium des rencontres client pour les Directeurs TI : gouvernance, cadre,
                    besoins, portail PRIsME-Client et trajectoire d’approbation.
                  </p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setIsGouvernanceTIOpen(true)}
                className="group bg-white border border-dashed border-sky-300 rounded-xl shadow-sm text-left overflow-hidden hover:border-sky-500 hover:shadow-lg transition-all"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center group-hover:bg-sky-600 transition-colors">
                      <Gavel className="w-6 h-6 text-sky-700 group-hover:text-white transition-colors" />
                    </div>
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                      En développement
                    </span>
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-sky-700 mb-2">
                    Projet PRIsME
                  </p>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    Gouvernance TI
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    Ébauche interactive pour structurer la gouvernance TI client : 6 piliers,
                    matrice RACI, rôles, contacts, organigramme et guide pédagogique.
                  </p>
                </div>
              </button>
            </div>
          </section>
        )}
      </main>

      <CadreReferentielModal
        isOpen={isCadreReferentielOpen}
        onClose={() => setIsCadreReferentielOpen(false)}
        hiddenTabIds={['methodologie', 'raison', 'solutions', 'spider', 'roadmap', 'gouvernance', 'client']}
        includeEbaucheCadreBZTabs
        hideHeaderStats
      />
      <ProjetEHTangiblesModal
        isOpen={isTangiblesOpen}
        onClose={() => setIsTangiblesOpen(false)}
      />
      <GouvernanceAgenticAIModal
        isOpen={isGouvernanceAgentiqueOpen}
        onClose={() => setIsGouvernanceAgentiqueOpen(false)}
      />
      <GouvernanceAgentiqueV2Modal
        isOpen={isGouvernanceAgentiqueV2Open}
        onClose={() => setIsGouvernanceAgentiqueV2Open(false)}
      />
      <GouvernanceTIModal
        isOpen={isGouvernanceTIOpen}
        onClose={() => setIsGouvernanceTIOpen(false)}
      />
      <PresentationPRIsMEModal
        isOpen={isPresentationPRIsMEOpen}
        onClose={() => setIsPresentationPRIsMEOpen(false)}
      />
      <PresentationDirecteurTIModal
        isOpen={isPresentationDirecteurTIOpen}
        onClose={() => setIsPresentationDirecteurTIOpen(false)}
      />
      <RigueurModal
        isOpen={isRigueurOpen}
        onClose={() => setIsRigueurOpen(false)}
      />
      {isTestModeleOdsOpen && (
        <PriseDeBesoinWizardModal
          isOpen={isTestModeleOdsOpen}
          onClose={() => setIsTestModeleOdsOpen(false)}
          templateId="ods-support-gestion"
          templateVersion="1.0.0"
          onSuccess={(besoinId) => {
            console.log('Prise de besoin ODS sauvegardée depuis Projet PRIsME:', besoinId);
          }}
        />
      )}
      {isPriseBesoinClientOpen && (
        <PriseDeBesoinWizardModal
          isOpen={isPriseBesoinClientOpen}
          onClose={() => setIsPriseBesoinClientOpen(false)}
          templateId="prise-de-besoin-client-2026"
          templateVersion="3.1.0"
          onSuccess={(besoinId) => {
            console.log('Prise de besoin - Maturité et besoins client sauvegardée depuis Projet PRIsME:', besoinId);
          }}
        />
      )}
    </div>
  );
}

function RigueurModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [activeBehaviorIndex, setActiveBehaviorIndex] = useState(0);

  if (!isOpen) return null;

  const activeBehavior = RIGUEUR_BEHAVIORS[activeBehaviorIndex];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/70 backdrop-blur-sm">
      <div className="min-h-full px-4 py-8">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <header className="bg-slate-950 px-6 py-6 text-white md:px-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-200">
                  Projet PRIsME
                </p>
                <h2 className="mt-2 text-2xl font-bold md:text-3xl">
                  Ébauche de rigueur
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-300">
                  Dix comportements concrets pour rendre le travail plus constant, plus traçable et
                  plus facile à piloter dans PRIsME.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-white/15 bg-white/10 p-2 text-white transition hover:bg-white/20"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </header>

          <main className="bg-slate-50 p-5 md:p-8">
            <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
              <nav className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" aria-label="Comportements de rigueur">
                <div className="space-y-2">
                  {RIGUEUR_BEHAVIORS.map((behavior, index) => {
                    const isActive = index === activeBehaviorIndex;
                    return (
                      <button
                        key={behavior.title}
                        type="button"
                        onClick={() => setActiveBehaviorIndex(index)}
                        className={`flex w-full items-start gap-3 rounded-lg border px-3 py-3 text-left transition ${
                          isActive
                            ? 'border-violet-300 bg-violet-50 text-violet-950 shadow-sm'
                            : 'border-transparent text-slate-700 hover:border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <span
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                            isActive ? 'bg-violet-700 text-white' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="text-sm font-semibold leading-snug">{behavior.title}</span>
                      </button>
                    );
                  })}
                </div>
              </nav>

              <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
                <div className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-6 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase text-emerald-700">
                      <CheckCircle2 className="h-4 w-4" />
                      Comportement {String(activeBehaviorIndex + 1).padStart(2, '0')}
                    </div>
                    <h3 className="text-2xl font-bold leading-tight text-slate-950">
                      {activeBehavior.title}
                    </h3>
                  </div>
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                    <ListChecks className="h-6 w-6 text-violet-700" />
                  </div>
                </div>

                <div className="space-y-5">
                  <article className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                    <h4 className="mb-2 text-sm font-bold uppercase text-slate-900">
                      Description du comportement
                    </h4>
                    <p className="text-sm leading-relaxed text-slate-700">{activeBehavior.description}</p>
                  </article>

                  <article className="rounded-lg border border-amber-200 bg-amber-50 p-5">
                    <h4 className="mb-2 text-sm font-bold uppercase text-amber-900">
                      Raison d’être
                    </h4>
                    <p className="text-sm leading-relaxed text-amber-950">{activeBehavior.raison}</p>
                  </article>

                  <article className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
                    <h4 className="mb-2 text-sm font-bold uppercase text-emerald-900">
                      Comment le mettre en œuvre
                    </h4>
                    <p className="text-sm leading-relaxed text-emerald-950">{activeBehavior.miseEnOeuvre}</p>
                  </article>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
